import { catalogues } from "./i18n.js";
/** `*_key` fields of a manifest that hold a prefix or a row's property, not a word. */
const NOT_WORDS = new Set(['values_key', 'second_values_key', 'row_key', 'pack_key']);
/**
 * Every catalogue key a screen manifest draws: each `*_key` field anywhere in it (a title, a lede,
 * a column, a field's hint, an action's effect, an empty list's words…), the word of each value a
 * column gives a tone to (`value.{column}.{value}`, or under its `values_key`), and each fixed tab.
 */
export function screenWordKeys(screen) {
    const keys = new Set();
    const walk = (v) => {
        if (Array.isArray(v)) {
            v.forEach(walk);
            return;
        }
        if (!v || typeof v !== 'object')
            return;
        for (const [k, x] of Object.entries(v)) {
            if (k.endsWith('_key') && typeof x === 'string' && x && !NOT_WORDS.has(k))
                keys.add(x);
            else
                walk(x);
        }
    };
    walk(screen);
    const m = (screen ?? {});
    for (const c of m.columns ?? []) {
        if (!c.key || c.display === 'progress')
            continue; // a progress bar's tones are bands, not values
        for (const value of Object.keys(c.tones ?? {}))
            keys.add(c.values_key ? `${c.values_key}.${value}` : `value.${c.key}.${value}`);
    }
    if (m.tabs?.by)
        for (const v of m.tabs.values ?? [])
            keys.add(`value.${m.tabs.by}.${v}`);
    return [...keys].sort();
}
/**
 * Every catalogue key a module manifest (`module.json`) gives the words of what it declares for
 * another module to draw: each document type it puts through the approval engine
 * (`approval_types[].label_key`), each fact of one and each option of a choice. The engine sends
 * them beside the declared English, and the renderer words the cell, the choice and the refusal by
 * them (`value_key_from`, `label_key_from`), so a key missing here reaches the reader in English.
 */
export function moduleWordKeys(module) {
    const keys = new Set();
    const add = (v) => { if (typeof v === 'string' && v)
        keys.add(v); };
    const types = module?.approval_types;
    for (const t of Array.isArray(types) ? types : []) {
        const type = (t ?? {});
        add(type.label_key);
        for (const f of Array.isArray(type.facts) ? type.facts : []) {
            const fact = (f ?? {});
            add(fact.label_key);
            for (const o of Array.isArray(fact.options) ? fact.options : [])
                add(o?.label_key);
        }
    }
    return [...keys].sort();
}
const LOCALES = ['ar', 'en'];
/**
 * What is wrong with a unit's words, or nothing:
 * - a key the unit's catalogue has in one language and not the other, or with an empty message;
 * - a key the unit's catalogue rewords from the core's: the core's word wins in the shell, so the
 *   unit's is never shown (the same word, while the key moves from the core to the unit, is fine);
 * - a key a screen draws, a module declares (`moduleWordKeys`: a document type and its facts), or one
 *   of `keys` — the words the unit's code and refusals say — that is in neither the core's catalogue
 *   nor the unit's, in either language.
 */
export function unitCatalogueProblems({ screens = [], modules = [], own, keys = [], core = catalogues }) {
    const problems = [];
    const mine = { ar: own.ar ?? {}, en: own.en ?? {} };
    for (const locale of LOCALES) {
        const other = locale === 'ar' ? 'en' : 'ar';
        for (const [key, word] of Object.entries(mine[locale])) {
            if (typeof word !== 'string' || !word)
                problems.push({ key, locale, message: `${locale}.json has an empty message for "${key}"` });
            if (!(key in mine[other]))
                problems.push({ key, locale: other, message: `"${key}" is in ${locale}.json and missing from ${other}.json` });
            if (key in core[locale] && core[locale][key] !== word) {
                problems.push({ key, locale, message: `"${key}" is a core word: the core's «${core[locale][key]}» is shown, never the unit's «${word}» — drop it from ${locale}.json, or use a key of the unit's own` });
            }
        }
    }
    const wanted = [
        ...screens.flatMap((s) => {
            const name = s?.screen;
            return screenWordKeys(s).map((key) => (typeof name === 'string' ? { key, screen: name } : { key }));
        }),
        ...modules.flatMap((m) => {
            const name = m?.key;
            return moduleWordKeys(m).map((key) => (typeof name === 'string' ? { key, module: name } : { key }));
        }),
        ...keys.map((key) => ({ key })),
    ];
    const seen = new Set();
    for (const { key, screen, module } of wanted) {
        for (const locale of LOCALES) {
            if (seen.has(`${locale}|${key}`))
                continue;
            seen.add(`${locale}|${key}`);
            if (core[locale][key] || mine[locale][key])
                continue;
            problems.push({ key, locale, ...(screen ? { screen } : {}), ...(module ? { module } : {}),
                message: `${screen ? `${screen} draws` : module ? `module ${module} declares` : 'the unit draws'} "${key}", which neither the core's catalogue nor the unit's ${locale}.json has` });
        }
    }
    return problems;
}
/** Prefixes the shell draws outside any one module's screen: always in the common chunk. */
export const COMMON_WORD_PREFIXES = ['nav.', 'group.', 'category.', 'family.', 'module.', 'entity.', 'hub.', 'unit.', 'state.', 'list.', 'action.', 'app.'];
export function splitUnitCatalogue(words, modules) {
    const users = new Map();
    const use = (key, module) => { let s = users.get(key); if (!s) {
        s = new Set();
        users.set(key, s);
    } s.add(module); };
    const columnsOf = new Map();
    // The entity each module's change record names (`audit.{table}.entity`): `change.field.{entity}.…`
    // words are drawn in that module's history.
    const auditsOf = new Map();
    for (const m of modules) {
        for (const t of Object.values((m.module ?? {}).audit ?? {})) {
            if (typeof t?.entity !== 'string')
                continue;
            let s = auditsOf.get(t.entity);
            if (!s) {
                s = new Set();
                auditsOf.set(t.entity, s);
            }
            s.add(m.key);
        }
        for (const k of screenWordKeys(m.module ?? {}))
            use(k, m.key);
        for (const k of moduleWordKeys(m.module ?? {}))
            use(k, m.key);
        for (const screen of m.screens) {
            for (const k of screenWordKeys(screen))
                use(k, m.key);
            for (const c of (screen ?? {}).columns ?? []) {
                if (typeof c?.key !== 'string')
                    continue;
                let s = columnsOf.get(c.key);
                if (!s) {
                    s = new Set();
                    columnsOf.set(c.key, s);
                }
                s.add(m.key);
            }
        }
    }
    const keys = new Set(modules.map((m) => m.key));
    const out = { common: {}, modules: {} };
    const put = (module, key, word) => {
        if (!module) {
            out.common[key] = word;
            return;
        }
        (out.modules[module] ??= {})[key] = word;
    };
    for (const [key, word] of Object.entries(words)) {
        if (COMMON_WORD_PREFIXES.some((p) => key.startsWith(p))) {
            put(null, key, word);
            continue;
        }
        const said = users.get(key);
        if (said) {
            put(said.size === 1 ? [...said][0] : null, key, word);
            continue;
        }
        const parts = key.split('.');
        if (parts[0] === 'value' && parts.length >= 3) {
            const owners = columnsOf.get(parts[1]);
            put(owners && owners.size === 1 ? [...owners][0] : null, key, word);
            continue;
        }
        if (parts[0] === 'change' && parts[1] === 'field' && parts.length >= 4) {
            const owners = auditsOf.get(parts[2]);
            put(owners && owners.size === 1 ? [...owners][0] : null, key, word);
            continue;
        }
        // Under a module's name in one of its first three places: `leave.…`, `hr.leave.…`, `hr.refusal.leave.…`.
        const own = parts.slice(0, 3).slice(0, parts.length - 1).find((p) => keys.has(p)) ?? null;
        put(own, key, word);
    }
    return out;
}
/**
 * The index of a unit's split catalogues and the files it names (`{locale}/{name}`, relative to
 * `i18n/`), each named by a hash of its content (`hash`, the build's own — a short hex digest), so a
 * chunk can be served as immutable and a changed word is a new name.
 */
export function catalogueIndex(splits, hash) {
    const files = {};
    const index = { v: 1, locales: {} };
    for (const [locale, split] of Object.entries(splits)) {
        const file = (name, words) => {
            const text = JSON.stringify(words);
            const named = `${name}.${hash(text)}.json`;
            files[`${locale}/${named}`] = text;
            return named;
        };
        const common = file('common', split.common);
        const modules = {};
        for (const [m, words] of Object.entries(split.modules).sort(([x], [y]) => x.localeCompare(y)))
            modules[m] = file(m, words);
        index.locales[locale] = { common, modules };
    }
    return { index, files };
}
//# sourceMappingURL=unit-catalogue.js.map